import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Film, ChevronDown, Check, Plus, ListTree, Lock } from 'lucide-react';
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from '../ui/popover';
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandSeparator,
} from '../ui/command';
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from '../ui/tooltip';
import { useWorkspace } from '../../context/WorkspaceContext';
import { useFilm } from '../../context/FilmContext';
import { useWorkspaceFilms } from '../../hooks/queries/useWorkspaceFilms';
import { buildFilmSwitchUrl } from './urlHelpers';
import { cn } from '../../lib/utils';

const SEARCH_THRESHOLD = 5;

/**
 * FilmPicker — the [🎬 Film Title ▾] segment of the top-bar Context Switcher.
 *
 * - Disabled (greyed + tooltip) when no workspace is selected.
 * - Lists films of currentWorkspace via useWorkspaceFilms (TanStack Query).
 * - On select — calls Phase 1 setCurrentFilmId + navigates with subpath
 *   preservation (see buildFilmSwitchUrl).
 */
export default function FilmPicker() {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentWorkspace, currentWorkspaceId } = useWorkspace();
  const { currentFilmId, setCurrentFilmId } = useFilm();
  const [open, setOpen] = useState(false);

  const disabled = !currentWorkspaceId;
  const filmsQuery = useWorkspaceFilms(currentWorkspaceId, { enabled: !disabled });
  const films = filmsQuery.data || [];

  // Selected-film label — prefer the entry in the freshly-fetched list, fall
  // back to anything provided through context, finally to a placeholder.
  const selectedFilm = films.find((f) => f.id === currentFilmId);
  const label = selectedFilm?.title || (currentFilmId ? '…' : 'Select film');

  const hasNoFilms = !disabled && !filmsQuery.isLoading && films.length === 0;
  const showSearch = films.length > SEARCH_THRESHOLD;

  const handleSelect = (filmId) => {
    setOpen(false);
    if (!filmId) return;
    if (filmId === currentFilmId) return;
    setCurrentFilmId(filmId);
    const url = buildFilmSwitchUrl(location.pathname, currentWorkspaceId, filmId);
    navigate(url);
  };

  const handleCreate = () => {
    setOpen(false);
    if (currentWorkspaceId) {
      // Existing create-film flow lives inside WorkspaceHome — land users
      // there and they can click the in-page CTA. (Phase 3 may upgrade
      // this to a direct dialog open via query param.)
      navigate(`/workspaces/${currentWorkspaceId}`);
    }
  };

  const handleViewAll = () => {
    setOpen(false);
    if (currentWorkspaceId) navigate(`/workspaces/${currentWorkspaceId}`);
  };

  // ── Disabled trigger (no workspace selected) ────────────────────────
  if (disabled) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            disabled
            aria-label="Film selection disabled — pick a workspace first"
            data-testid="ctx-switcher-film-trigger"
            data-disabled="true"
            className={cn(
              'inline-flex items-center gap-1.5 max-w-[180px] md:max-w-[220px]',
              'h-8 px-2 rounded-md text-sm font-medium',
              'text-muted-foreground/60 cursor-not-allowed bg-transparent'
            )}
          >
            <Lock className="h-3.5 w-3.5 shrink-0" aria-hidden />
            <span className="truncate">Select film</span>
            <ChevronDown className="h-3.5 w-3.5 shrink-0 opacity-40" aria-hidden />
          </button>
        </TooltipTrigger>
        <TooltipContent>Select a workspace first</TooltipContent>
      </Tooltip>
    );
  }

  // ── Enabled trigger ─────────────────────────────────────────────────
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label="Select film"
          data-testid="ctx-switcher-film-trigger"
          data-state={open ? 'open' : 'closed'}
          className={cn(
            'group inline-flex items-center gap-1.5 max-w-[180px] md:max-w-[220px]',
            'h-8 px-2 rounded-md text-sm font-medium',
            'text-foreground hover:bg-muted/60',
            'data-[state=open]:bg-muted',
            'transition-colors outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1'
          )}
        >
          <Film className="h-4 w-4 text-muted-foreground shrink-0" aria-hidden />
          <span className="truncate">{label}</span>
          <ChevronDown
            className={cn(
              'h-3.5 w-3.5 text-muted-foreground shrink-0 opacity-60 transition-transform',
              'group-hover:opacity-100 group-data-[state=open]:rotate-180'
            )}
            aria-hidden
          />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={6}
        className="w-80 p-0"
        data-testid="ctx-switcher-film-popover"
      >
        <Command>
          {showSearch && (
            <CommandInput placeholder="Search films…" aria-label="Search films" />
          )}
          <CommandList>
            <CommandEmpty>No films found.</CommandEmpty>

            {filmsQuery.isLoading && (
              <div className="py-6 text-center text-xs text-muted-foreground">
                Loading films…
              </div>
            )}

            {filmsQuery.isError && (
              <div className="py-6 text-center text-xs text-destructive">
                Failed to load films
              </div>
            )}

            {!filmsQuery.isLoading && hasNoFilms && (
              <div className="py-6 px-3 text-center text-xs text-muted-foreground">
                No films yet in <strong className="text-foreground">{currentWorkspace?.name || 'this workspace'}</strong>.
              </div>
            )}

            {films.length > 0 && (
              <CommandGroup heading={currentWorkspace?.name || 'Films'}>
                {films.map((film) => (
                  <CommandItem
                    key={film.id}
                    value={`${film.title} ${film.id}`}
                    onSelect={() => handleSelect(film.id)}
                    data-testid={`ctx-switcher-film-option-${film.id}`}
                  >
                    <Film className="h-4 w-4 text-muted-foreground" aria-hidden />
                    <span className="truncate flex-1">{film.title}</span>
                    {film.id === currentFilmId && (
                      <Check className="h-4 w-4 text-primary" aria-label="current" />
                    )}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}

            <CommandSeparator />
            <CommandGroup>
              <CommandItem
                onSelect={handleCreate}
                data-testid="ctx-switcher-film-create"
              >
                <Plus className="h-4 w-4" aria-hidden />
                <span>Create film</span>
              </CommandItem>
              {films.length > 0 && (
                <CommandItem
                  onSelect={handleViewAll}
                  data-testid="ctx-switcher-film-view-all"
                >
                  <ListTree className="h-4 w-4" aria-hidden />
                  <span>View all films</span>
                </CommandItem>
              )}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
